import { Component, computed, inject, isDevMode } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { extractDigitsAsNumber } from './pace-spector.utils';
import { PaceInsightsService } from '@evorait/pace-logging';
import { AlertComponent } from 'ngx-bootstrap/alert';
import { TabDirective, TabsetComponent } from 'ngx-bootstrap/tabs';
import { addressesById, selectAddresses } from '../../state/address';
import { Store } from '@ngrx/store';
import { AddressSpectorNgrxComponent } from './address-spector-ngrx/address-spector-ngrx.component';
import { selectUserFrontId } from '../../state/config';
import {
    allTimesheetsRemoved,
    selectSelectedTimesheet,
    selectTimesheets,
    selectTimesheetSummaries,
    timesheetRemoved,
    timesheetSelected,
} from '../../state/timesheet';
import { selectNotificationEntries } from '../../state';
import { TimesheetSpectorNgrxComponent } from './timesheet-spector-ngrx/timesheet-spector-ngrx.component';
import { getUuidV4 } from '../../utilities/generic-utilities';
import { TimesheetPickerComponent } from '../../../../../../apps/pace/src/app/timesheet/components/timesheet-ngrx/timesheet-picker/timesheet-picker.component';

@Component({
    selector: 'pace-core-pace-spector-ngrx',
    imports: [
        CommonModule,
        TranslateModule,
        FormsModule,
        AlertComponent,
        TabsetComponent,
        TabDirective,
        AddressSpectorNgrxComponent,
        TimesheetSpectorNgrxComponent,
        TimesheetPickerComponent,
    ],
    templateUrl: './pace-spector-ngrx.component.html',
    styleUrl: './pace-spector.component.css',
})
export class PaceSpectorNgrxComponent {
    private readonly store = inject(Store);
    private readonly paceInsightsService = inject(PaceInsightsService);

    public unlocked = false;
    public session = this.paceInsightsService.getClientSession();
    public gateLabelId = 'pace-spector-gate-label';
    public gateInputId = 'pace-spector-gate-input';
    public errors: string[] = [];

    public accessCode: string | null = null;

    public addresses = this.store.selectSignal(selectAddresses);
    public addressesById = computed(() => addressesById(this.addresses()));
    public timesheets = this.store.selectSignal(selectTimesheets);
    public frontId = this.store.selectSignal(selectUserFrontId);
    public notifications = this.store.selectSignal(selectNotificationEntries);

    protected readonly summaries = this.store.selectSignal(selectTimesheetSummaries);
    protected readonly selectedTimesheet = this.store.selectSignal(selectSelectedTimesheet);

    public unlock(): void {
        const parsed = this.accessCode?.trim();
        if (!parsed?.length) {
            return;
        }
        let accessCode = 67;

        const today = new Date();
        const dayPart = today.getDate();
        const monthPart = today.getMonth() + 1;
        accessCode += (dayPart + monthPart) * 2;

        const frontId = this.frontId();
        if (frontId?.length > 3) {
            const fronNum = extractDigitsAsNumber(frontId);
            if (fronNum) {
                accessCode += fronNum;
            }
        }

        if (parsed === accessCode.toString() || (isDevMode() && parsed === '-3010')) {
            this.unlocked = true;
            this.paceInsightsService.trackEvent('PaceSpector::unlock', { status: 'success' });
        }
    }

    public downloadJson(data: unknown): void {
        const json = JSON.stringify(
            {
                frontId: this.frontId(),
                session: this.paceInsightsService.getClientSession(),
                data,
            },
            null,
            2,
        );
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `pace-inspector-${new Date().toISOString()}.json`;

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(url);
    }

    protected onTimesheetSelected(timesheetId: string): void {
        this.store.dispatch(timesheetSelected({ timesheetId }));
    }

    protected deleteTimesheet(timesheetId: string): void {
        this.store.dispatch(timesheetRemoved({ timesheetId, correlationId: getUuidV4() }));
    }

    protected deleteAllTimesheets(): void {
        this.store.dispatch(allTimesheetsRemoved({ correlationId: getUuidV4() }));
    }
}
